import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useSiteTexts, t, useDeliveryConfig } from "../contexts/SiteTextsContext";

function AccordionItem({ question, children, isOpen, onClick }) {
  return (
    <div className="border-b border-[#3D2B1F]/10">
      <button className="w-full py-5 flex items-center justify-between text-left" onClick={onClick}>
        <h3 className="text-base sm:text-lg text-[#3D2B1F] pr-6">{question}</h3>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#C4A4A0] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#C4A4A0] flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="pb-5 animate-fadeIn">
          <div className="text-[#3D2B1F]/70 leading-relaxed font-light space-y-3 text-sm sm:text-base">{children}</div>
        </div>
      )}
    </div>
  );
}

const PROCESS_STEPS_AUSPOST = [
  { num: 1, color: "#9A7B4F", title: "Browse & Choose", desc: "Explore our collection and find the piece that speaks to you. Each outfit includes detailed photos, measurements, and styling suggestions." },
  { num: 2, color: "#B8C4B8", title: "Select Your Event Date", desc: "Tell us when your event is. We'll automatically reserve the outfit for a 6-day window — giving you time to receive, wear, and return it comfortably." },
  { num: 3, color: "#D4B8B4", title: "Receive & Shine", desc: "Pick up locally in Melbourne SE or have it delivered to your door. Wear it, create memories, feel amazing." },
  { num: 4, color: "#6B5B4F", title: "Return Happy", desc: "Your rental includes a prepaid Australia Post return label. Simply place the outfit back in the box it arrived in, tape on the return label, and drop it off at your local Australia Post Office. Make sure to hand it over the counter so it's scanned — this is your proof of return." },
  { num: 5, color: "#C4A4A0", title: "Bond Returned", desc: "Once we receive and inspect the outfit, your security bond* is returned. We handle all the cleaning and care — the outfit is then ready for its next adventure." },
];

const PROCESS_STEPS_LOCAL = [
  { num: 1, color: "#9A7B4F", title: "Browse & Choose", desc: "Explore our collection and find the piece that speaks to you. Each outfit includes detailed photos, measurements, and styling suggestions." },
  { num: 2, color: "#B8C4B8", title: "Select Your Event Date", desc: "Tell us when your event is. We'll automatically reserve the outfit for a 6-day window — giving you time to receive, wear, and return it comfortably." },
  { num: 3, color: "#D4B8B4", title: "Receive & Shine", desc: "Pick up from us or get it delivered to your door the day before your event. Wear it, create memories, feel amazing." },
  { num: 4, color: "#6B5B4F", title: "Return Happy", desc: "After your event, we'll arrange a convenient pick-up or drop-off time. Book your return appointment and we'll take care of the rest — cleaning, care, everything. Return postage is the customer's responsibility unless Mahra confirms otherwise in writing." },
  { num: 5, color: "#C4A4A0", title: "Bond Returned", desc: "Once we receive and inspect the outfit, your security bond* is returned. The outfit is then ready for its next adventure." },
];

export default function BorrowingTerms() {
  const [openIndex, setOpenIndex] = useState(0);
  const texts = useSiteTexts();
  const { auspost_content_visible } = useDeliveryConfig();
  const PROCESS_STEPS = auspost_content_visible ? PROCESS_STEPS_AUSPOST : PROCESS_STEPS_LOCAL;

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative overflow-x-hidden">
      <SEO
        title="How It Works - Borrowing Terms"
        description="How to rent designer Indian outfits from Mahra. Simple 4-step process: Browse, Book, Wear, Return. Delivery & pickup in Melbourne."
        path="/borrowing-terms"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Borrowing Terms" }]}
      />
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-10 pb-6 lg:px-16 lg:pt-20 lg:pb-10 text-center">
        <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-3">Simple Process</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl text-[#3D2B1F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
          {t(texts, "borrowing_hero_title", "Borrowing Made Easy")}
        </h1>
        <p className="text-[#3D2B1F]/60 font-light max-w-xl mx-auto text-sm sm:text-base">
          {t(texts, "borrowing_hero_subtitle", "Four simple steps from browsing to borrowing. No stress, no fuss.")}
        </p>
      </section>

      {/* Process Steps */}
      <section className="px-4 sm:px-6 py-8 lg:px-16 lg:py-14" data-testid="borrowing-process-steps">
        <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8">
          {PROCESS_STEPS.map((step) => (
            <div key={step.num} className="flex gap-4 sm:gap-6 items-start" data-testid={`process-step-${step.num}`}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full text-white flex items-center justify-center flex-shrink-0 font-medium text-sm sm:text-base" style={{ backgroundColor: step.color }}>
                {step.num}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <h3 className="text-lg sm:text-xl text-[#676664] mb-1.5">{step.title}</h3>
                <p className="text-[#676664]/60 font-light text-sm sm:text-base leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Filler 1: Brand Punchline */}
      <section className="px-4 sm:px-6 py-12 lg:px-16 lg:py-20 bg-[#6B5B4F]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl sm:text-3xl lg:text-4xl text-white/90 font-light leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t(texts, "borrowing_punchline_1", "Look stunning. Spend less. Return happy.")}
          </p>
          <p className="text-white/50 text-sm sm:text-base font-light mt-4 max-w-lg mx-auto">
            {t(texts, "borrowing_punchline_1_sub", "Every outfit in our collection has been hand-picked and loved. Now it's your turn to make memories in it.")}
          </p>
        </div>
      </section>

      {/* Filler 2: Trust markers */}
      <section className="px-4 sm:px-6 py-10 lg:px-16 lg:py-16 bg-[#B8C4B8]/10">
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
          <div>
            <p className="text-2xl sm:text-3xl text-[#9A7B4F] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>100%</p>
            <p className="text-[#676664]/60 text-sm font-light mt-1">{t(texts, "borrowing_trust_1", "Refundable bond on every rental")}</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl text-[#9A7B4F] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Free</p>
            <p className="text-[#676664]/60 text-sm font-light mt-1">{t(texts, "borrowing_trust_2", "Pick up from Melbourne SE")}</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl text-[#9A7B4F] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>$15</p>
            <p className="text-[#676664]/60 text-sm font-light mt-1">{t(texts, "borrowing_trust_3", "Local delivery within 15km")}</p>
          </div>
        </div>
      </section>

      {/* Terms Accordion */}
      <section className="px-4 sm:px-6 py-10 lg:px-16 lg:py-14" data-testid="borrowing-terms">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-[#C4A4A0] text-[10px] tracking-[0.3em] uppercase mb-2">The Fine Print</p>
            <h2 className="text-2xl sm:text-3xl text-[#3D2B1F]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t(texts, "borrowing_terms_title", "Borrowing Terms")}
            </h2>
          </div>

          <AccordionItem question="Cancellations" isOpen={openIndex === 0} onClick={() => setOpenIndex(openIndex === 0 ? -1 : 0)}>
            <p className="font-medium text-[#3D2B1F]">More than 7 days before your event date</p>
            <p>A full refund will be issued.</p>
            <p className="font-medium text-[#3D2B1F] pt-2">Between 4-7 days before your event date</p>
            <p>A 50% refund will be issued.</p>
            <p className="font-medium text-[#3D2B1F] pt-2">3 days or less before your event date</p>
            <p>Bookings are non-refundable.</p>
            <p className="pt-3 text-[#3D2B1F]/60 italic">Once your outfit has been dispatched, cancellations are no longer available under any circumstances.</p>
            <p className="text-[13px] text-[#3D2B1F]/50 pt-2">All timeframes are calculated based on the event date provided at the time of booking.</p>
          </AccordionItem>

          <AccordionItem question="Outfit Swaps" isOpen={openIndex === 1} onClick={() => setOpenIndex(openIndex === 1 ? -1 : 1)}>
            <p>You may request to change your selected outfit at least 7 days prior to your event date, subject to availability.</p>
            <p className="pt-2">If the new piece is higher in price, the difference must be paid.<br />If lower in price, the balance will be issued as store credit.<br />One swap is permitted per booking.</p>
            <p className="pt-3 text-[#3D2B1F]/60 italic">Swaps are not available within 7 days of the event date.</p>
          </AccordionItem>

          <AccordionItem question="Date Changes" isOpen={openIndex === 2} onClick={() => setOpenIndex(openIndex === 2 ? -1 : 2)}>
            <p>One date change may be requested more than 7 days prior to your event date, subject to availability.</p>
            <p className="pt-2">Once a booking date has been changed, the reservation becomes final and is no longer eligible for cancellation or refund.</p>
            <p className="pt-3 text-[#3D2B1F]/60 italic">Date changes are not permitted within 7 days of the event date.</p>
          </AccordionItem>

          <AccordionItem question="Refundable Bond" isOpen={openIndex === 3} onClick={() => setOpenIndex(openIndex === 3 ? -1 : 3)}>
            <p>A refundable bond is held for each booking and returned once the garment has been received and inspected.</p>
            <p className="pt-2">Minor signs of wear consistent with normal use are acceptable. Professional cleaning is handled by us.</p>
            <p className="pt-2">The bond is released after inspection provided the garment is returned in its original condition, subject to normal wear.</p>
          </AccordionItem>

          <AccordionItem question="Damage & Loss" isOpen={openIndex === 4} onClick={() => setOpenIndex(openIndex === 4 ? -1 : 4)}>
            <p>If a garment is returned with damage beyond normal wear, the cost of repair will be deducted from the refundable bond.</p>
            <p className="pt-2">If damage is deemed irreparable — including but not limited to permanent staining, tearing, burning, structural damage, alteration, missing components, or any condition rendering the garment unsuitable for future rental — the full Recommended Retail Price (RRP) of the garment will become payable.</p>
            <p className="pt-2">The refundable bond will be applied toward this amount, and any remaining balance must be settled within 7 days of notice.</p>
            <p className="pt-2">If a garment is lost, not returned, or returned in a condition that prevents restoration to rental standard, it will be treated as a total loss and charged at full RRP.</p>
            <p className="pt-3 text-[#3D2B1F]/60 italic">Mahra reserves sole discretion in determining whether a garment is repairable or irreparable.</p>
          </AccordionItem>
        </div>
      </section>

      {/* Filler 3: Quote */}
      <section className="px-4 sm:px-6 py-10 lg:px-16 lg:py-16 bg-[#F0D9D5]/20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xl sm:text-2xl text-[#676664] italic font-light leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t(texts, "borrowing_quote", "\"Borrow beautiful. Return happy. That's the Mahra way.\"")}
          </p>
        </div>
      </section>

      {/* CTA: Browse + Contact */}
      <section className="px-4 sm:px-6 py-12 lg:px-16 lg:py-20 bg-white" data-testid="borrowing-cta">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl text-[#3D2B1F] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {t(texts, "borrowing_cta_title", "Your next outfit is waiting")}
          </h2>
          <p className="text-[#3D2B1F]/60 font-light mb-8 text-sm sm:text-base max-w-lg mx-auto">
            {t(texts, "borrowing_cta_subtitle", "Browse our curated collection and find the perfect piece for your next event. Have a question? We'd love to hear from you.")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/catalog" className="inline-flex items-center gap-2 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 py-3.5 text-[11px] tracking-[0.15em] uppercase transition-colors" data-testid="browse-collection-btn">
              Browse Collection <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5 px-8 py-3.5 text-[11px] tracking-[0.15em] uppercase transition-colors" data-testid="contact-us-btn">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
